// ZIP archives, read and written without a library: the format an applet is distributed as (docs/applets.md). Shared
// by the page (client/lib/zip.js, with the browser's DecompressionStream) and the server (server/lib/zip.js, with
// node:zlib), so it takes the (de)compressor as a function and touches no DOM or Node API itself. Stored and
// deflated entries only (what every zip tool writes); no ZIP64, no encryption, no spanning: an archive needing those
// is refused with a readable message. Entry names are checked as package paths (applet-model.js), so nothing read
// here can name a file outside the package.
import { FILES_MAX, isPackagePath, PACKAGE_MAX } from './applet-model.js';

const SIG_LOCAL = 0x04034b50, SIG_CENTRAL = 0x02014b50, SIG_END = 0x06054b50;
const STORED = 0, DEFLATED = 8;
const fail = (message) => { throw new Error(message); };
const utf8 = new TextDecoder();
const encode = (s) => new TextEncoder().encode(s);

/**
 * The files of a ZIP: Map of path -> Uint8Array, folders left out. `inflate(bytes) -> Promise<Uint8Array>` inflates a
 * raw deflate stream. Throws on anything but a well-formed archive of acceptable paths and sizes.
 */
export async function parseZip(bytes, inflate) {
  if (!(bytes instanceof Uint8Array)) bytes = new Uint8Array(bytes);
  if (bytes.length > PACKAGE_MAX) fail(`The package is too big (${Math.round(PACKAGE_MAX / 1048576)} MB at most)`);
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  // the end-of-central-directory record is in the last 64 KB (its comment can be that long)
  let end = -1;
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 65557); i--) {
    if (dv.getUint32(i, true) === SIG_END) { end = i; break; }
  }
  if (end < 0) fail('Not a ZIP file');
  if (dv.getUint16(end + 4, true) !== 0 || dv.getUint16(end + 6, true) !== 0) fail('A ZIP spanning several files is not supported');
  const count = dv.getUint16(end + 10, true);
  const dirSize = dv.getUint32(end + 12, true);
  const dirAt = dv.getUint32(end + 16, true);
  if (count === 0xffff || dirAt === 0xffffffff || dirSize === 0xffffffff) fail('ZIP64 archives are not supported');
  if (count > FILES_MAX) fail(`The package has too many files (${FILES_MAX} at most)`);
  if (dirAt + dirSize > end) fail('Not a ZIP file (bad central directory)');
  const files = new Map();
  let total = 0;
  let at = dirAt;
  for (let n = 0; n < count; n++) {
    if (at + 46 > end || dv.getUint32(at, true) !== SIG_CENTRAL) fail('Not a ZIP file (bad entry)');
    const flags = dv.getUint16(at + 8, true);
    const method = dv.getUint16(at + 10, true);
    const compressed = dv.getUint32(at + 20, true);
    const size = dv.getUint32(at + 24, true);
    const nameLen = dv.getUint16(at + 28, true), extraLen = dv.getUint16(at + 30, true), commentLen = dv.getUint16(at + 32, true);
    const localAt = dv.getUint32(at + 42, true);
    const name = utf8.decode(bytes.subarray(at + 46, at + 46 + nameLen));
    at += 46 + nameLen + extraLen + commentLen;
    if (flags & 0x1) fail(`The package is encrypted (${name})`);
    if (name.endsWith('/')) continue; // a folder entry
    if (!isPackagePath(name)) fail(`The package has a file it may not have: ${name.slice(0, 80)}`);
    if (method !== STORED && method !== DEFLATED) fail(`Unsupported compression in the package (${name})`);
    if (localAt + 30 > bytes.length || dv.getUint32(localAt, true) !== SIG_LOCAL) fail('Not a ZIP file (bad local header)');
    const dataAt = localAt + 30 + dv.getUint16(localAt + 26, true) + dv.getUint16(localAt + 28, true);
    if (dataAt + compressed > bytes.length) fail('Not a ZIP file (truncated)');
    total += size;
    if (total > PACKAGE_MAX) fail(`The package is too big unpacked (${Math.round(PACKAGE_MAX / 1048576)} MB at most)`);
    const raw = bytes.subarray(dataAt, dataAt + compressed);
    const data = method === STORED ? raw.slice() : await inflate(raw);
    if (data.length !== size) fail(`The package is damaged (${name})`);
    if (crc32(data) !== dv.getUint32(at - nameLen - extraLen - commentLen - 46 + 16, true)) fail(`The package is damaged (${name}: checksum)`);
    files.set(name, data);
  }
  return files;
}

/**
 * A ZIP of `files` (Map or object of path -> Uint8Array), every entry deflated by `deflate(bytes) -> Promise<Uint8Array>`
 * (raw deflate), or stored when `deflate` is null. Paths are written as given, sorted.
 */
export async function buildZip(files, deflate) {
  const entries = [...(files instanceof Map ? files : Object.entries(files))].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  const parts = [], central = [];
  let offset = 0;
  const [date, time] = dosDateTime(new Date());
  for (const [path, bytes] of entries) {
    if (!isPackagePath(path)) fail(`Cannot package a file at ${path}`);
    const data = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    const name = encode(path);
    const crc = crc32(data);
    const packed = deflate ? await deflate(data) : data;
    const method = deflate ? DEFLATED : STORED;
    const local = new Uint8Array(30 + name.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, SIG_LOCAL, true); lv.setUint16(4, 20, true); lv.setUint16(6, 0x800, true); // UTF-8 names
    lv.setUint16(8, method, true); lv.setUint16(10, time, true); lv.setUint16(12, date, true);
    lv.setUint32(14, crc, true); lv.setUint32(18, packed.length, true); lv.setUint32(22, data.length, true);
    lv.setUint16(26, name.length, true); lv.setUint16(28, 0, true);
    local.set(name, 30);
    parts.push(local, packed);
    const c = new Uint8Array(46 + name.length);
    const cv = new DataView(c.buffer);
    cv.setUint32(0, SIG_CENTRAL, true); cv.setUint16(4, 20, true); cv.setUint16(6, 20, true); cv.setUint16(8, 0x800, true);
    cv.setUint16(10, method, true); cv.setUint16(12, time, true); cv.setUint16(14, date, true);
    cv.setUint32(16, crc, true); cv.setUint32(20, packed.length, true); cv.setUint32(24, data.length, true);
    cv.setUint16(28, name.length, true); cv.setUint32(42, offset, true);
    c.set(name, 46);
    central.push(c);
    offset += local.length + packed.length;
  }
  const dirSize = central.reduce((n, c) => n + c.length, 0);
  const endRec = new Uint8Array(22);
  const ev = new DataView(endRec.buffer);
  ev.setUint32(0, SIG_END, true); ev.setUint16(8, central.length, true); ev.setUint16(10, central.length, true);
  ev.setUint32(12, dirSize, true); ev.setUint32(16, offset, true);
  const all = [...parts, ...central, endRec];
  const out = new Uint8Array(all.reduce((n, p) => n + p.length, 0));
  let p = 0;
  for (const part of all) { out.set(part, p); p += part.length; }
  return out;
}

function dosDateTime(d) {
  const date = ((Math.max(1980, d.getFullYear()) - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate();
  const time = (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1);
  return [date, time];
}
let table = null;
export function crc32(bytes) {
  if (!table) {
    table = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      table[n] = c >>> 0;
    }
  }
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) crc = table[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}
